import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { AppConstants } from 'src/app/constants/app-constants';
import { ColumnDef } from 'src/app/model/column-definition';
import { BeyondSupplier } from 'src/app/model/beyond-supplier';
import { BeyondSupplierInterface } from 'src/app/model/beyond-supplier-interface';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { BeyondSupplierService } from 'src/app/services/beyond-supplier/beyond-supplier.service';

/**
 * BeyondSupplierPageComponent represents the beyond suppliers page
 */
@Component({
  selector: 'app-beyond-supplier-page',
  templateUrl: './beyond-supplier-page.component.html',
  styleUrl: './beyond-supplier-page.component.scss'
})
export class BeyondSupplierPageComponent implements AfterViewInit, OnDestroy, OnInit {
  /** The paginator to apply to the table */
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator | null = null;

  /** The sort element in our table */
  @ViewChild(MatSort, {static: true}) sort: MatSort = new MatSort();

  /** The table of beyond suppliers */
  @ViewChild(MatTable) table!: MatTable<BeyondSupplier>;

  /** The data to be rendered in the table */
  public source = new MatTableDataSource<BeyondSupplier>([]);

  /** How long it has been since the last refresh */
  public timeSinceLastRefresh: string = "0:00";

  /** Subscription to update new beyond suppliers */
  private _sub: Subscription | null = null;

  /** Column definitions for the table */
  public colDefs: ColumnDef[] = [
    {header: "ID", field: "primaryCompanyId"},
    {header: "Type", field: "primaryCompanyType"},
    {header: "Name", field: "fullName"},
    {header: "Email", field: "email"},
    {header: "Group", field: "group"},
    {header: "Status", field: "status", format: "titlecase"},
    {header: "Actions", field: "actions", format: "actions"},
  ]

  public displayedCols: string[] = this.colDefs.map(x => x.field);

  /**
   * Constructor for the beyond supplier page to initialize services and set a base snapshot 
   * 
   * @param _beyondSupplierService 
   */
  constructor(
    private _beyondSupplierService: BeyondSupplierService,
    private _dialogService: DialogService,
    private _router: Router,
  ) {
    // Subscribe to timer changes
    this._beyondSupplierService.timeSinceLastRefresh$.subscribe(
      (str: string) => {
        this.timeSinceLastRefresh = str;
      });
  }

  /**
   * Initialize the sort, paginator, and beyond supplier data source
   */
  public ngOnInit() {
    this.source.sort = this.sort;
    this.source.paginator = this.paginator;

    // Add a filter predicate to search with our custom toString method with delimieters
    this.source.filterPredicate = (data: BeyondSupplier, filter: string): boolean => {
      const str = data.toString(this.colDefs);

      // Hotfix to allow for filtering better between active/deactivated
      if (filter.length > 0 && "active".startsWith(filter)) {
        return str.replaceAll("deactivated", "").includes(filter);
      }
      return str.includes(filter);
    }

    // Get the data when it loads and update the snapshot values
    this._sub = this._beyondSupplierService.beyondSuppliers$.subscribe(
      (resp: BeyondSupplier[]) => {
        this._populateSource(resp);
      });

    this._populateSource(this._beyondSupplierService.getBeyondSuppliers());
  }

  /**
   * After the table gets initialized, add a sorter and custom sorting functions
   */
  public ngAfterViewInit() {
    this.source.sortingDataAccessor = (item: BeyondSupplier, property: string): string | number => {
      switch (property) {
        default: return (item as any)[property];
      }
    }
  }

  /**
   * When the component gets destroyed (user navigates away, unsubscribe from changes)
   */
  public ngOnDestroy() {
    this._sub?.unsubscribe();
  }

  /**
   * Edit a beyond supplier
   */
  public editBeyondSupplier(beyondSup: any): void {
    const beyondSupplier = beyondSup as BeyondSupplier;

    const id = beyondSupplier.id;

    this._dialogService.openEditBeyondSupplier(beyondSupplier).subscribe((updated: BeyondSupplier): void => {
      if (updated == undefined) return;
      this._beyondSupplierService.updateBeyondSupplier(id, updated);
    });

    console.log("Edit beyond supplier:", beyondSupplier);
  }

  /**
   * Suspend a beyond supplier
   */
  public suspendBeyondSupplier(beyondSup: any): void {
    const beyondSupplier = beyondSup as BeyondSupplier;

    this._dialogService.openSuspendBeyondSupplier(beyondSupplier).subscribe((confirmed: boolean): void => {
      if (!confirmed) return;
      this._beyondSupplierService.suspendBeyondSupplier(beyondSupplier.id);
    });

    console.log("Suspend beyond supplier:", beyondSupplier);
  }

  /**
   * Terminate a beyond supplier
   */
  public terminateBeyondSupplier(beyondSup: any): void {
    const beyondSupplier = beyondSup as BeyondSupplier;

    this._dialogService.openTerminateBeyondSupplier(beyondSupplier).subscribe((confirmed: boolean): void => {
      if (!confirmed) return;
      this._beyondSupplierService.terminateBeyondSupplier(beyondSupplier.id);
    });

    console.log("Terminate beyond supplier:", beyondSupplier);
  }

  /**
   * Apply a filter to the table
   */
  public applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.source.filter = filterValue.trim().toLowerCase();
  }

  /**
   * Populate the data source meticulously so it doesn't lag the beyond supplier
   * Note: I don't know if async is required, I'm just paranoid
   */
  private async _populateSource(incoming: BeyondSupplier[]): Promise<void> {
    // Don't update the table when the user is not on the page => angular will be mad :(
    if (this._router.url.replace("/", "") !== AppConstants.MENU_BEYOND_SUPPLIERS) return;

    let source = this.source.data;
    const current = this.source.data.map((x: BeyondSupplier) => x.copyOf());

    // For each incoming beyond supplier, determine if it exists in the source. 
    const update = async () => {
      let sourceIndex = 0; // Using currentIndex isn't reliable because we need to decrement it

      for (let currentIndex = 0; currentIndex < current.length; currentIndex++) {
        for (let incomingIndex = 0; incomingIndex < incoming.length; incomingIndex++) {
          // If beyond supplier IDs are equal check for updates and remove from the lists
          if (incoming[incomingIndex].id === current[currentIndex].id) {

            // If the beyond suppliers are not equal, update the current source to the new one
            if (!incoming[incomingIndex].equals(current[currentIndex])) {
              source[sourceIndex] = incoming[incomingIndex].copyOf();
            }

            // We've updated the beyond supplier, so remove it from both tracking arrays
            incoming.splice(incomingIndex, 1);
            current.splice(currentIndex, 1);

            currentIndex--;
            break;
          }
        }

        sourceIndex++;
      }
    }
    await update();

    // Add new beyond suppliers that we don't currently have
    const add = async () => {
      for (let newBeyondSupplier of incoming) {
        source.push(newBeyondSupplier);
      }
    }
    await add();

    // Remove old beyond suppliers that have been removed
    const remove = async () => {
      for (let oldBeyondSupplier of current) {
        source = source.filter(x => x.id !== oldBeyondSupplier.id);
      }
    }
    await remove();

    // Update the table
    this.source.data = source;
  }
}
